import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client.js';
import { auth, projects as projectsApi } from '../api/resources.js';
import { Banner, Card, Empty, ErrorState, Loading } from '../components/ui.js';
import { useToast } from '../components/toast.js';
import { useAction, useAsync } from '../lib/hooks.js';

interface PrivacyInventory {
  classes: Array<{ name: string; kind: string; count?: number; retentionDays?: number }>;
  retention: Array<{ dataClass: string; retentionDays: number }>;
  subprocessors: Array<{ name: string; purpose: string; region: string }>;
}

/** Configurações, projetos e privacidade (PRD §22, §109 a §111). */
export function SettingsPage() {
  const toast = useToast();
  const [projectName, setProjectName] = useState('');
  const [environment, setEnvironment] = useState('production');
  const [deletion, setDeletion] = useState({ password: '', reason: '' });
  const [confirming, setConfirming] = useState(false);

  const navigate = useNavigate();
  const me = useAsync(() => auth.me(), []);
  const projects = useAsync(() => projectsApi.list(), []);

  // Segurança: segundo fator, senha e e-mail. Tudo que muda quem pode entrar pede a senha
  // atual — a sessão prova quem está logado, a senha prova quem é.
  const [enrolment, setEnrolment] = useState<{ secret: string; uri: string } | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaOff, setMfaOff] = useState({ password: '', code: '' });
  const [passwords, setPasswords] = useState({ current: '', next: '' });
  const [emailChange, setEmailChange] = useState({ email: '', password: '' });

  const startMfa = useAction(async () => {
    setEnrolment(await auth.mfaSetup());
    setMfaCode('');
  });
  const confirmMfa = useAction(async () => {
    await auth.mfaConfirm(mfaCode);
    toast.success('Segundo fator ativo', 'A partir do próximo login, pediremos o código.');
    setEnrolment(null);
    setMfaCode('');
    me.reload();
  });
  const disableMfa = useAction(async () => {
    await auth.mfaDisable(mfaOff.password, mfaOff.code);
    toast.success('Segundo fator desligado');
    setMfaOff({ password: '', code: '' });
    me.reload();
  });
  const changePassword = useAction(async () => {
    await auth.changePassword(passwords.current, passwords.next);
    toast.success('Senha alterada');
    setPasswords({ current: '', next: '' });
  });
  const changeEmail = useAction(async () => {
    await auth.changeEmail(emailChange.password, emailChange.email);
    toast.success('E-mail alterado', 'Enviamos um código para o endereço novo.');
    setEmailChange({ email: '', password: '' });
    navigate('/verify-email');
  });
  const privacy = useAsync(() => api<PrivacyInventory>('/v1/privacy/inventory'), []);

  const createProject = useAction(async () => {
    await projectsApi.create(projectName, environment);
    toast.success('Projeto criado');
    setProjectName('');
    projects.reload();
  });

  const requestDeletion = useAction(async () => {
    const result = await api<{ scheduledFor: string; graceDays: number }>('/v1/privacy/deletion', {
      method: 'POST',
      body: deletion,
    });
    toast.success(
      'Exclusão agendada',
      `Você tem ${result.graceDays} dias para cancelar. Os dados seguem acessíveis.`,
    );
    setConfirming(false);
    setDeletion({ password: '', reason: '' });
  });

  if (me.loading) return <Loading />;

  return (
    <>
      <Card title="Organização">
        <div className="stack">
          <div className="spread">
            <span className="muted">Organização</span>
            <code className="mono">{me.data?.organizationId}</code>
          </div>
          <div className="spread">
            <span className="muted">Seu papel</span>
            <span className="pill pill-brand">{me.data?.role}</span>
          </div>
          <div className="spread">
            <span className="muted">Modo de autenticação</span>
            <span className="pill pill-neutral">{me.data?.authMode}</span>
          </div>
        </div>
      </Card>

      <Card
        title="Segurança"
        description="Segundo fator, senha e e-mail de acesso. Toda mudança pede a senha atual."
      >
        <div className="stack">
          <section>
            <div className="spread">
              <div>
                <strong>Segundo fator (TOTP)</strong>
                <p className="muted m-0">
                  {me.data?.mfaEnabled
                    ? 'Ativo: o login pede um código do seu autenticador.'
                    : 'Desligado: só a senha abre a sua conta.'}
                </p>
              </div>
              <span className={`pill ${me.data?.mfaEnabled ? 'pill-ok' : 'pill-neutral'}`}>
                {me.data?.mfaEnabled ? 'Ativo' : 'Desligado'}
              </span>
            </div>

            {!me.data?.mfaEnabled && !enrolment && (
              <button
                type="button"
                className="btn btn-primary"
                disabled={startMfa.pending}
                onClick={() => void startMfa.run()}
              >
                Ativar segundo fator
              </button>
            )}
            {startMfa.error != null && <ErrorState error={startMfa.error} />}

            {!me.data?.mfaEnabled && enrolment && (
              <form
                className="stack"
                onSubmit={(event) => {
                  event.preventDefault();
                  void confirmMfa.run();
                }}
              >
                <p className="muted m-0">
                  Cadastre este segredo no seu autenticador (Google Authenticator, 1Password, Authy)
                  e digite o primeiro código para confirmar.
                </p>
                <div className="field">
                  <label htmlFor="mfa-secret">Segredo</label>
                  <input id="mfa-secret" className="mono" value={enrolment.secret} readOnly />
                </div>
                <a className="muted" href={enrolment.uri}>
                  Abrir no autenticador
                </a>
                <div className="field">
                  <label htmlFor="mfa-code">Primeiro código</label>
                  <input
                    id="mfa-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={mfaCode}
                    onChange={(event) => setMfaCode(event.target.value)}
                    required
                    minLength={6}
                    maxLength={8}
                  />
                </div>
                {confirmMfa.error != null && <ErrorState error={confirmMfa.error} />}
                <div className="row">
                  <button type="submit" className="btn btn-primary" disabled={confirmMfa.pending}>
                    Confirmar
                  </button>
                  <button type="button" className="btn" onClick={() => setEnrolment(null)}>
                    Cancelar
                  </button>
                </div>
              </form>
            )}

            {me.data?.mfaEnabled && (
              <form
                className="form-row"
                onSubmit={(event) => {
                  event.preventDefault();
                  void disableMfa.run();
                }}
              >
                <div className="field">
                  <label htmlFor="mfa-off-password">Senha atual</label>
                  <input
                    id="mfa-off-password"
                    type="password"
                    autoComplete="current-password"
                    value={mfaOff.password}
                    onChange={(event) => setMfaOff((c) => ({ ...c, password: event.target.value }))}
                    required
                  />
                </div>
                <div className="field">
                  <label htmlFor="mfa-off-code">Código do autenticador</label>
                  <input
                    id="mfa-off-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={mfaOff.code}
                    onChange={(event) => setMfaOff((c) => ({ ...c, code: event.target.value }))}
                    required
                    minLength={6}
                    maxLength={8}
                  />
                </div>
                <button type="submit" className="btn btn-danger" disabled={disableMfa.pending}>
                  Desligar segundo fator
                </button>
                {disableMfa.error != null && <ErrorState error={disableMfa.error} />}
              </form>
            )}
          </section>

          <section>
            <strong>Trocar senha</strong>
            <form
              className="form-row"
              onSubmit={(event) => {
                event.preventDefault();
                void changePassword.run();
              }}
            >
              <div className="field">
                <label htmlFor="password-current">Senha atual</label>
                <input
                  id="password-current"
                  type="password"
                  autoComplete="current-password"
                  value={passwords.current}
                  onChange={(event) => setPasswords((c) => ({ ...c, current: event.target.value }))}
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="password-next">Nova senha (mín. 12 caracteres)</label>
                <input
                  id="password-next"
                  type="password"
                  autoComplete="new-password"
                  value={passwords.next}
                  onChange={(event) => setPasswords((c) => ({ ...c, next: event.target.value }))}
                  required
                  minLength={12}
                />
              </div>
              <button type="submit" className="btn" disabled={changePassword.pending}>
                Alterar senha
              </button>
              {changePassword.error != null && <ErrorState error={changePassword.error} />}
            </form>
          </section>

          <section>
            <strong>Trocar e-mail</strong>
            <p className="muted m-0">
              Hoje: <code className="mono">{me.data?.user?.email ?? '—'}</code>. O endereço novo
              recebe um código, e até confirmá-lo a conta não cria instância nem API key.
            </p>
            <form
              className="form-row"
              onSubmit={(event) => {
                event.preventDefault();
                void changeEmail.run();
              }}
            >
              <div className="field">
                <label htmlFor="email-next">Novo e-mail</label>
                <input
                  id="email-next"
                  type="email"
                  autoComplete="email"
                  value={emailChange.email}
                  onChange={(event) => setEmailChange((c) => ({ ...c, email: event.target.value }))}
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="email-password">Senha atual</label>
                <input
                  id="email-password"
                  type="password"
                  autoComplete="current-password"
                  value={emailChange.password}
                  onChange={(event) =>
                    setEmailChange((c) => ({ ...c, password: event.target.value }))
                  }
                  required
                />
              </div>
              <button type="submit" className="btn" disabled={changeEmail.pending}>
                Alterar e-mail
              </button>
              {changeEmail.error != null && <ErrorState error={changeEmail.error} />}
            </form>
          </section>
        </div>
      </Card>

      <Card title="Projetos" description="Separe ambientes e integrações dentro da organização.">
        <form
          className="form-row"
          onSubmit={(event) => {
            event.preventDefault();
            void createProject.run();
          }}
        >
          <div>
            <label htmlFor="project-name">Nome</label>
            <input
              id="project-name"
              value={projectName}
              onChange={(event) => setProjectName(event.target.value)}
              placeholder="Homologação"
              required
              minLength={2}
            />
          </div>
          <div>
            <label htmlFor="project-env">Ambiente</label>
            <select
              id="project-env"
              value={environment}
              onChange={(event) => setEnvironment(event.target.value)}
            >
              <option value="production">production</option>
              <option value="staging">staging</option>
              <option value="development">development</option>
            </select>
          </div>
          <button type="submit" className="btn btn-primary" disabled={createProject.pending}>
            Criar projeto
          </button>
        </form>

        <div style={{ marginTop: 16 }}>
          {(projects.data?.data ?? []).map((project) => (
            <div key={project.id} className="spread" style={{ padding: '6px 0' }}>
              <span>{project.name}</span>
              <span className="pill pill-neutral">{project.environment}</span>
            </div>
          ))}
        </div>
        {createProject.error != null && <ErrorState error={createProject.error} />}
      </Card>

      <Card title="Privacidade e dados" description="O que a Talkium armazena e por quanto tempo.">
        {privacy.loading ? (
          <Loading />
        ) : privacy.error ? (
          <ErrorState error={privacy.error} onRetry={privacy.reload} />
        ) : (
          <div className="stack">
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Classe de dado</th>
                    <th>Classificação</th>
                    <th>Retenção</th>
                  </tr>
                </thead>
                <tbody>
                  {(privacy.data?.retention ?? []).map((entry) => (
                    <tr key={entry.dataClass}>
                      <td>{entry.dataClass}</td>
                      <td className="subtle">
                        {privacy.data?.classes.find((c) =>
                          c.name.toLowerCase().includes(entry.dataClass.split('_')[0] ?? ''),
                        )?.kind ?? 'INTERNAL'}
                      </td>
                      <td>{entry.retentionDays} dias</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div>
              <strong>Subprocessadores</strong>
              {(privacy.data?.subprocessors ?? []).map((sub) => (
                <div key={sub.name} className="spread" style={{ padding: '4px 0' }}>
                  <span>{sub.name}</span>
                  <span className="subtle">
                    {sub.purpose} · {sub.region}
                  </span>
                </div>
              ))}
            </div>

            <a className="btn btn-sm" href="/v1/privacy/export">
              Exportar meus dados
            </a>
          </div>
        )}
      </Card>

      <Card title="Encerrar conta" description="Exclusão com período de carência de 30 dias.">
        {!confirming ? (
          <>
            <p className="muted">
              Durante a carência sua conta fica restrita, mas os dados continuam legíveis e
              exportáveis. Você pode cancelar a qualquer momento.
            </p>
            <button type="button" className="btn btn-danger" onClick={() => setConfirming(true)}>
              Solicitar exclusão
            </button>
          </>
        ) : (
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              void requestDeletion.run();
            }}
          >
            <Banner tone="danger">
              Isto agenda a exclusão de toda a organização. Apenas um Owner pode fazê-lo.
            </Banner>
            <div className="field">
              <label htmlFor="deletion-password">Confirme sua senha</label>
              <input
                id="deletion-password"
                type="password"
                value={deletion.password}
                onChange={(event) => setDeletion((c) => ({ ...c, password: event.target.value }))}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="deletion-reason">Motivo (opcional)</label>
              <input
                id="deletion-reason"
                value={deletion.reason}
                onChange={(event) => setDeletion((c) => ({ ...c, reason: event.target.value }))}
              />
            </div>
            {requestDeletion.error != null && <ErrorState error={requestDeletion.error} />}
            <div className="row">
              <button type="submit" className="btn btn-danger" disabled={requestDeletion.pending}>
                Confirmar exclusão
              </button>
              <button type="button" className="btn" onClick={() => setConfirming(false)}>
                Cancelar
              </button>
            </div>
          </form>
        )}
      </Card>
    </>
  );
}

export function NotFoundPage() {
  return (
    <Card>
      <Empty title="Página não encontrada">
        <p className="muted">O endereço acessado não existe no console.</p>
        <a className="btn btn-sm" href="/">
          Voltar à visão geral
        </a>
      </Empty>
    </Card>
  );
}
